import { ChangeEvent, KeyboardEvent, useRef, useState } from "react";
import styled from "styled-components";
import useEscape from "../../hooks/useEscape";
import Flex from "../Layout/Flex";

type EditableTextTypes = "input" | "number";

interface Props {
	onChange?: (str: string) => any;
	className?: string;
	value?: string;
	onSave?: (str: string) => any;
	placeholder?: string;
	showPlaceholderInViewMode?: boolean;
	disableEditing?: boolean;
	type: EditableTextTypes;
	suffix?: string;
	inputWidth?: number | string;
	editing?: boolean;
	displayValueFormatter?: (val?: string) => string | undefined;
	validate?: (val?: string) => boolean;
}

export const EditableText: React.FC<Props> = ({
	editing = false,
	onChange,
	className,
	type,
	value,
	suffix,
	onSave,
	inputWidth,
	disableEditing = false,
	placeholder = "",
	showPlaceholderInViewMode = true,
	displayValueFormatter,
	validate,
}) => {
	const [editable, setEditable] = useState(editing);
	const inputRef = useRef<HTMLInputElement>(null);
	const [updatedValue, setUpdatedValue] = useState(value);
	const [invalid, setInvalid] = useState(false);

	function makeEditable() {
		if (disableEditing) return;

		setEditable(true);

		setTimeout(() => {
			if (inputRef.current) {
				inputRef.current.focus();
				inputRef.current.select();
			}
		}, 1);
	}

	function hasFocus() {
		return inputRef.current === document.activeElement;
	}

	useEscape({ onEscape: cancel });

	function cancel() {
		if (hasFocus()) {
			setEditable(false);
			setUpdatedValue(value?.trim());
		}
	}

	function save() {
		const isInvalid = validate && !validate(updatedValue);
		setInvalid(!!isInvalid);
		if (isInvalid) {
			// setUpdatedValue(value?.trim());
			return;
		}

		setEditable(false);

		onChange && onChange((updatedValue || "").trim());
		onSave && onSave((updatedValue || "").trim());
	}

	function onChangeInput(e: ChangeEvent<HTMLInputElement>) {
		setUpdatedValue(e.target.value);
	}

	function onKeyDownInput(e: KeyboardEvent<HTMLInputElement>) {
		if (e.key === "Enter") {
			e.preventDefault();
			e.stopPropagation();
			save();
		}
	}

	function renderInput() {
		const commonProps = {
			value: updatedValue,
			onChange: onChangeInput,
			placeholder: placeholder || "",
			style: { width: inputWidth },
			onBlur: save,
		};

		if (type === "input") {
			return <input type="text" {...commonProps} ref={inputRef} onKeyDown={onKeyDownInput} />;
		} else if (type === "number") {
			return <input type="number" {...commonProps} ref={inputRef} onKeyDown={onKeyDownInput} />;
		}

		// Add textarea here if ever needed to
	}

	return (
		<Styles
			className={`${className ?? ""} ${disableEditing ? "disabled" : ""} ${
				invalid ? "invalid" : ""
			}`}
		>
			{!editable ? (
				<>
					<p onClick={makeEditable}>
						{displayValueFormatter
							? displayValueFormatter(updatedValue)
							: updatedValue || (
									<span className="placeholder">
										{showPlaceholderInViewMode ? placeholder : ""}
									</span>
							  )}
						{suffix}
					</p>
				</>
			) : (
				<Flex alignItems="center" gap="0.5rem">
					{renderInput()}
					{suffix}
				</Flex>
			)}
		</Styles>
	);
};

const Styles = styled.div`
	margin: 0;
	width: 100%;

	p {
		white-space: pre-wrap;
		cursor: pointer;

		&:hover {
			text-decoration: underline;
		}
	}
	&.disabled p {
		cursor: default;
		color: ${(props) => props.theme.colors.COLOR_GRAY_TEXT_LIGHT};

		&:hover {
			text-decoration: none;
		}
	}

	input {
		display: block;
		border: 1px solid ${(props) => props.theme.colors.INPUT_BORDER_FOCUSED};
		overflow: auto;
		outline: none;
		resize: none;
		font-size: 1rem;
		width: 100%;
		color: ${(props) => props.theme.colors.text};
		background-color: #f9f9f9;
		padding: 0.25rem 0.5rem;
		border-radius: 2px;
	}

	.placeholder {
		color: ${(props) => props.theme.colors.COLOR_GRAY_TEXT_LIGHT};
	}

	&.invalid {
		input {
			background-color: ${(props) => props.theme.colors.COLOR_DANGER_LIGHT};
		}
	}
`;
